/**
 * Advanced Cart Performance Manager
 * Implements request batching, optimistic UI updates, and real-time performance monitoring
 */
class CartPerformanceManager {
  constructor() {
    // Debug: Track constructor calls
    if (!window.cartPerformanceManagerInstances) window.cartPerformanceManagerInstances = 0;
    window.cartPerformanceManagerInstances++;

    this.requestQueue = [];
    this.batchTimeout = null;
    this.batchDelay = 100; // ms
    this.isProcessingBatch = false;
    
    // Performance tracking
    this.metrics = {
      requestCount: 0,
      batchedRequests: 0,
      optimisticUpdates: 0,
      averageResponseTime: 0,
      cacheHitRate: 0,
      errorRate: 0,
      lastOperationTime: 0
    };
    
    // ICR-specific tracking (Phase 1)
    this.icrMetrics = {
      requestCount: 0,
      successCount: 0,
      errorCount: 0,
      averageResponseTime: 0,
      lastRequestTime: 0,
      adapterProductId: null,
      lineItemSources: new Map() // Track which line items triggered ICR additions
    };
    
    // Phase 3: Recent ICR completion tracking to prevent unnecessary refreshes
    this.recentICRCompletions = new Map(); // lineItemKey -> completion timestamp
    this.icrRefreshGracePeriod = 5000; // 5 seconds to skip refreshes after ICR completion
    
    // Optimistic UI state
    this.optimisticState = new Map();
    this.pendingOperations = new Set();

    this.init();
  }

  init() {
    this.setupPerformanceObserver();
    this.setupVisibilityTracking();
    this.interceptCartRequests();
    
    // Retry fetch override installation after a delay if it failed due to conflicts
    if (!window.fetch._cartPerformanceWrapped) {

      setTimeout(() => {
        this.interceptCartRequests();
      }, 1000);
    }
  }

  /**
   * Intercept and batch cart requests for better performance
   */
  interceptCartRequests() {
    // Check if fetch has already been overridden to prevent double-wrapping
    if (window.fetch._cartPerformanceWrapped) {
      console.warn('⚠️ WARNING: Cart performance fetch override already installed! Skipping duplicate override.');
      return;
    }
    
    // Check if another service (like Klaviyo) has wrapped fetch
    const fetchString = window.fetch.toString();
    const isKlaviyoWrapped = fetchString.includes('klaviyo.com') || fetchString.includes('back-in-stock');
    
    if (isKlaviyoWrapped) {
      console.warn('⚠️ WARNING: Klaviyo has already wrapped window.fetch - this may cause duplicate ICR requests!');
      
    }
    
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function(url, options = {}) {
      // Only batch cart add requests, but exempt ICR and cart drawer requests
      if (typeof url === 'string' && url.includes('/cart/add') && options.method === 'POST') {
        // Check if this is an ICR request - don't batch these
        const isICRRequest = options.headers && (
          options.headers['X-ICR-Request'] || 
          options.headers['x-icr-request']
        );
        
        if (isICRRequest) {
          return self.handleICRRequest(url, options, originalFetch);
        }
        
        return self.batchRequest(url, options, originalFetch);
      }
      
      // Also exempt cart change requests from cart drawer from batching
      if (typeof url === 'string' && url.includes('/cart/change') && options.method === 'POST') {
        const isCartDrawerRequest = options.headers && (
          options.headers['X-Cart-Drawer'] || 
          options.headers['x-cart-drawer']
        );
        
        if (isCartDrawerRequest) {
          return originalFetch.call(window, url, options);
        }
      }
      
      return originalFetch.call(window, url, options);
    };
    
    // Mark the fetch as wrapped to prevent double-wrapping
    window.fetch._cartPerformanceWrapped = true;
    window.fetch._originalFetch = originalFetch;

  }

  /**
   * Handle ICR requests with enhanced logging and tracking (Phase 1)
   */
  async handleICRRequest(url, options, originalFetch) {
    // EMERGENCY DEDUPLICATION: Check if this exact request is already in progress
    const requestKey = JSON.stringify({ url, body: options.body, headers: options.headers });
    
    const now = Date.now();
    
    // Clean up old entries (older than 5 seconds)
    if (!this._activeICRRequests) this._activeICRRequests = new Map();
    for (const [key, timestamp] of this._activeICRRequests.entries()) {
      if (now - timestamp > 5000) {
        this._activeICRRequests.delete(key);
      }
    }
    
    // Check if this exact request is already processing
    if (this._activeICRRequests.has(requestKey)) {
      console.warn(`🚨 DUPLICATE ICR REQUEST BLOCKED! Same request already in progress.`);
      
      
      // Return a fake successful response to prevent the duplicate from reaching the API
      const fakeResponse = new Response(JSON.stringify({
        success: true,
        message: 'Request deduplicated - FAKE RESPONSE',
        items: [],
        _duplicate_blocked: true
      }), {
        status: 200,
        statusText: 'OK (Deduplicated)',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      
      return fakeResponse;
    }
    
    // Mark this request as active
    this._activeICRRequests.set(requestKey, now);
    
    
    const requestId = Math.random().toString(36).substr(2, 9);
    const startTime = performance.now();
    this.icrMetrics.requestCount++;
    
    // Extract ICR request details for logging
    const requestDetails = this.extractICRRequestDetails(options);

    if (window.cartPerformanceDebug) {
      console.log(`🔧 ICR Request #${this.icrMetrics.requestCount} to ${url}`);
    }
    
    try {
      // Execute the original ICR request (no behavior change in Phase 1)
      const response = await originalFetch.call(window, url, options);
      const endTime = performance.now();
      const duration = endTime - startTime;

      
      // Track success metrics
      this.icrMetrics.successCount++;
      this.icrMetrics.lastRequestTime = duration;
      this.icrMetrics.averageResponseTime = (this.icrMetrics.averageResponseTime + duration) / 2;
      
      // Track line item source if available
      if (requestDetails.lineItemKey) {
        this.icrMetrics.lineItemSources.set(requestDetails.lineItemKey, {
          productId: requestDetails.sourceProductId,
          timestamp: Date.now(),
          duration: duration
        });
      }
      
      // Phase 3: Mark recent ICR completion to prevent unnecessary refreshes
      this.markRecentICRCompletion(requestDetails);

      // Remove from active requests map
      this._activeICRRequests.delete(requestKey);
      
      // CRITICAL FIX: Update cart sections after successful ICR addition
      this.updateCartSectionsAfterICR(response, requestDetails);
      
      if (window.cartPerformanceDebug) {
        console.log(`✅ ICR Request Successful (${duration.toFixed(0)}ms)`);
      }
      
      return response;
      
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Track error metrics
      this.icrMetrics.errorCount++;
      this.icrMetrics.lastRequestTime = duration;

      // Remove from active requests map even on error
      this._activeICRRequests.delete(requestKey);
      
      
      if (window.cartPerformanceDebug) {
        console.error(`❌ ICR Request Failed (${duration.toFixed(0)}ms):`, error.message);
      }
      
      throw error;
    }
  }

  /**
   * Update cart sections after successful ICR addition
   */
  async updateCartSectionsAfterICR(response, requestDetails) {
    try {
      if (window.cartPerformanceDebug) {
        console.log('🔄 Updating cart sections after ICR addition...');
      }

      // Fetch latest cart state with sections to update both cart drawer and main cart
      const sectionsToUpdate = [
        'cart-drawer',
        'cart-icon-bubble', 
        'main-cart-items',
        'main-cart-footer'
      ].join(',');

      const cartResponse = await fetch(`${window.routes.cart_url}?sections=${sectionsToUpdate}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!cartResponse.ok) {
        throw new Error(`Cart fetch failed: ${cartResponse.status}`);
      }

      const cartData = await cartResponse.json();
      
      if (window.cartPerformanceDebug) {
        console.log('📦 Cart sections received:', Object.keys(cartData).filter(key => key.includes('-')).length);
      }
      
      // CRITICAL FIX: The sections are direct properties of cartData, not nested under cartData.sections
      // Create a proper sections object for the section manager
      const normalizedCartData = {
        ...cartData,
        sections: {}
      };
      
      // Move section data to the sections object
      Object.keys(cartData).forEach(key => {
        if (key.includes('-') && typeof cartData[key] === 'string') {
          normalizedCartData.sections[key] = cartData[key];
        }
      });

      // Update cart drawer (sidecart) always - regardless of state
      // Target the active/visible cart drawer (not hidden duplicates)
      const cartDrawers = document.querySelectorAll('cart-drawer');
      let cartDrawer = null;
      
      if (cartDrawers.length > 1) {
        // Find the visible/active cart drawer
        cartDrawer = Array.from(cartDrawers).find(drawer => 
          !drawer.hasAttribute('hidden') && 
          drawer.offsetParent !== null && 
          window.getComputedStyle(drawer).display !== 'none'
        ) || cartDrawers[0]; // Fallback to first if none are clearly visible
        

      } else {
        cartDrawer = cartDrawers[0];
      }
      
      if (cartDrawer) {
        if (normalizedCartData.sections && normalizedCartData.sections['cart-drawer']) {
          
          const cartDrawerSections = [
            { id: 'cart-drawer', selector: '.shopify-section' }
          ];
          window.cartSectionManager.smartRender(cartDrawer, normalizedCartData, cartDrawerSections);
        }
      }

      // Update main cart page if we're on the cart page
      const mainCartItems = document.querySelector('#main-cart-items');
      const mainCartFooter = document.querySelector('#main-cart-footer');
      const cartItemsWrapper = document.querySelector('cart-items'); // The outer wrapper
      
      if (mainCartItems || mainCartFooter || cartItemsWrapper) {
        const mainCartSections = [];
        
        // UPDATE MAIN CART with proper ICR preservation
        if (mainCartItems && normalizedCartData.sections && normalizedCartData.sections['main-cart-items']) {
          mainCartSections.push({ id: 'main-cart-items', selector: '.js-contents' });
        }
        
        // Only update footer to get new totals
        if (mainCartFooter && normalizedCartData.sections && normalizedCartData.sections['main-cart-footer']) {
          // Footer updates temporarily disabled - using direct total updates instead
          // mainCartSections.push({ id: 'main-cart-footer', selector: '.shopify-section' });
        }
        
        // ALTERNATIVE: Direct cart total update as fallback
        if (mainCartFooter) {
          setTimeout(() => {
            this.updateCartTotalsDirectly(normalizedCartData);
          }, 100);
        }
        
        // ALTERNATIVE: Try to extract and update totals directly if section update fails
        if (mainCartFooter && normalizedCartData.sections && normalizedCartData.sections['main-cart-footer']) {
          setTimeout(() => {
            try {
              // Extract total from the updated section HTML
              const parser = new DOMParser();
              const doc = parser.parseFromString(normalizedCartData.sections['main-cart-footer'], 'text/html');
              const newTotal = doc.querySelector('.totals__subtotal-value');
              const currentTotal = mainCartFooter.querySelector('.totals__subtotal-value');
              
              if (newTotal && currentTotal && newTotal.textContent !== currentTotal.textContent) {
                currentTotal.textContent = newTotal.textContent;
              }
            } catch (error) {
              console.warn('⚠️ Direct total update failed:', error);
            }
          }, 100);
        }
        
        if (mainCartSections.length > 0) {
          // Use the first available element as the component reference
          const component = mainCartItems || mainCartFooter;
          window.cartSectionManager.smartRender(component, normalizedCartData, mainCartSections);
        }
        
        // ADDITIONAL: If we have the cart items wrapper but no main-cart-items section,
        // we might be dealing with the full cart-items section
        if (cartItemsWrapper && normalizedCartData.sections && normalizedCartData.sections['cart-items'] && !mainCartItems) {
          const cartSections = [{ id: 'cart-items', selector: '.page-width' }];
          window.cartSectionManager.smartRender(cartItemsWrapper, normalizedCartData, cartSections);
        }
      }

      // Update cart icon bubble
      if (normalizedCartData.sections && normalizedCartData.sections['cart-icon-bubble']) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const iconSections = [{ id: 'cart-icon-bubble', selector: '.shopify-section' }];
          window.cartSectionManager.smartRender(cartIconBubble, normalizedCartData, iconSections);
        }
      }

      if (window.cartPerformanceDebug) {
        console.log('✅ Cart sections updated');
      }
      
      // ADDITIONAL: Trigger custom events to notify other components
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { 
          cartData: normalizedCartData, 
          source: 'icr-addition',
          requestDetails: requestDetails 
        } 
      }));

    } catch (error) {
      console.error('❌ Failed to update cart sections after ICR addition:', error);
      
      // Fallback: Try a more aggressive cart refresh
      if (window.cartPerformanceDebug) {
        console.log('🔄 Attempting fallback cart refresh...');
      }
      
      try {
        // Try to trigger a cart refresh via existing cart functionality
        const cartForm = document.querySelector('#cart');
        if (cartForm) {
          const formData = new FormData(cartForm);
          
          fetch('/cart/update.js', {
            method: 'POST',
            body: formData
          }).then(response => response.json())
            .then(() => {
              // Reload the page to ensure fresh state
              window.location.reload();
            });
        } else {
          // No cart form found, just reload
          window.location.reload();
        }
      } catch (fallbackError) {
        console.error('❌ Fallback refresh also failed:', fallbackError);
        window.location.reload();
      }
    }
  }

  /**
   * Extract ICR request details for logging and tracking
   */
  extractICRRequestDetails(options) {
    const details = {
      isICRRequest: true,
      lineItemKey: null,
      sourceProductId: null,
      adapterProductId: null,
      hasLineItemContext: false
    };
    
    try {
      // Extract line item context from headers
      if (options.headers) {
        details.lineItemKey = options.headers['X-Line-Item-Context'] || options.headers['x-line-item-context'];
        details.hasLineItemContext = !!details.lineItemKey;
      }
      
      // Extract product IDs from JSON body (ICR requests use JSON, not FormData)
      if (typeof options.body === 'string') {
        try {
          const bodyData = JSON.parse(options.body);
          if (bodyData.items && bodyData.items[0]) {
            const item = bodyData.items[0];
            details.adapterProductId = item.id;
            
            // Extract from properties
            if (item.properties) {
              details.sourceProductId = item.properties['_icr_for_product'];
              details.lineItemKey = details.lineItemKey || item.properties['_icr_for_line_item'];
            }
          }
        } catch (parseError) {
          console.warn('Failed to parse ICR request body:', parseError);
        }
      }
      
      // Store adapter product ID for future reference
      if (details.adapterProductId && !this.icrMetrics.adapterProductId) {
        this.icrMetrics.adapterProductId = details.adapterProductId;
      }
      
    } catch (error) {
      console.warn('Failed to extract ICR request details:', error);
    }
    
    return details;
  }

  /**
   * Batch multiple rapid cart requests
   */
  async batchRequest(url, options, originalFetch) {
    return new Promise((resolve, reject) => {
      const requestData = {
        url,
        options,
        resolve,
        reject,
        timestamp: Date.now(),
        id: Math.random().toString(36).substr(2, 9)
      };

      this.requestQueue.push(requestData);
      
      // Clear existing timeout and set new one
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout);
      }
      
      this.batchTimeout = setTimeout(() => {
        this.processBatch(originalFetch);
      }, this.batchDelay);
    });
  }

  /**
   * Process batched requests efficiently
   */
  async processBatch(originalFetch) {
    if (this.isProcessingBatch || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingBatch = true;
    const batch = [...this.requestQueue];
    this.requestQueue = [];

    if (window.cartPerformanceDebug) {
      console.group(`⚡ Processing batch of ${batch.length} requests`);
    }

    try {
      if (batch.length === 1) {
        // Single request - process normally
        const request = batch[0];
        const response = await originalFetch(request.url, request.options);
        const result = await response.json();
        request.resolve(new Response(JSON.stringify(result), {
          status: response.status,
          headers: response.headers
        }));
      } else {
        // Multiple requests - batch them intelligently
        await this.processBatchedRequests(batch, originalFetch);
      }

      this.metrics.batchedRequests += batch.length;
      
    } catch (error) {
      batch.forEach(request => request.reject(error));
      this.metrics.errorRate++;
    } finally {
      this.isProcessingBatch = false;
      
      if (window.cartPerformanceDebug) {
        console.log(`✅ Batch completed in ${Date.now() - batch[0].timestamp}ms`);
        console.groupEnd();
      }
    }
  }

  /**
   * Intelligently process multiple cart requests
   */
  async processBatchedRequests(batch, originalFetch) {
    // CRITICAL FIX: Preserve sections parameter for proper cart rendering
    // Extract sections from the first request that has them
    let sectionsParam = null;
    let sectionsUrl = null;
    
    // Combine multiple add requests into single request when possible
    const combinedItems = [];
    
    for (const request of batch) {
      try {
        const formData = request.options.body;
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1');
        
        // CRITICAL: Preserve sections parameter from requests
        if (!sectionsParam && formData.get('sections')) {
          sectionsParam = formData.get('sections');
          sectionsUrl = formData.get('sections_url');
        }
        
        // Find existing item to combine quantities
        const existingItem = combinedItems.find(item => item.id === variantId);
        if (existingItem) {
          existingItem.quantity += quantity;
        } else {
          combinedItems.push({ id: variantId, quantity });
        }
      } catch (error) {
        console.warn('Failed to parse request for batching:', error);
      }
    }

    // Prepare request body with sections parameter preserved
    const requestBody = { items: combinedItems };
    if (sectionsParam) {
      requestBody.sections = sectionsParam;
      if (sectionsUrl) {
        requestBody.sections_url = sectionsUrl;
      }
    }

    // Make single combined request with sections preserved
    const combinedResponse = await originalFetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(requestBody)
    });

    const result = await combinedResponse.json();
    
    // Resolve all requests with the combined result
    batch.forEach(request => {
      request.resolve(new Response(JSON.stringify(result), {
        status: combinedResponse.status,
        headers: combinedResponse.headers
      }));
    });
  }

  /**
   * Implement optimistic UI updates for immediate feedback
   */
  async optimisticUpdate(operation, updateData) {
    const operationId = Math.random().toString(36).substr(2, 9);
    this.pendingOperations.add(operationId);
    
    try {
      // Apply optimistic update immediately
      this.applyOptimisticUpdate(operation, updateData);
      this.metrics.optimisticUpdates++;
      
      // Perform actual operation
      const result = await operation();
      
      // Confirm optimistic update or revert if needed
      this.confirmOptimisticUpdate(operationId, result);
      
      return result;
    } catch (error) {
      // Revert optimistic update on error
      this.revertOptimisticUpdate(operationId, updateData);
      throw error;
    } finally {
      this.pendingOperations.delete(operationId);
    }
  }

  /**
   * Apply immediate UI feedback before server confirmation
   */
  applyOptimisticUpdate(operation, updateData) {
    // Update cart count immediately
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(element => {
      const currentCount = parseInt(element.textContent) || 0;
      element.textContent = currentCount + (updateData.quantity || 1);
      element.classList.add('optimistic-update');
    });

    // Add visual feedback
    const submitButton = updateData.button;
    if (submitButton) {
      submitButton.classList.add('optimistic-success');
      setTimeout(() => {
        submitButton.classList.remove('optimistic-success');
      }, 1000);
    }
  }

  /**
   * Confirm optimistic update with server response
   */
  confirmOptimisticUpdate(operationId, result) {
    // Remove optimistic styling
    document.querySelectorAll('.optimistic-update').forEach(element => {
      element.classList.remove('optimistic-update');
    });

    // Update with actual server data if needed
    if (result && result.item_count !== undefined) {
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = result.item_count;
      });
    }
  }

  /**
   * Revert optimistic update on error
   */
  revertOptimisticUpdate(operationId, originalData) {
    // Revert cart count
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(element => {
      const currentCount = parseInt(element.textContent) || 0;
      element.textContent = Math.max(0, currentCount - (originalData.quantity || 1));
      element.classList.remove('optimistic-update');
      element.classList.add('optimistic-error');
      
      setTimeout(() => {
        element.classList.remove('optimistic-error');
      }, 2000);
    });
  }

  /**
   * Set up performance monitoring
   */
  setupPerformanceObserver() {
    // Monitor cart-related performance
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.name.includes('cart') || entry.name.includes('/cart/')) {
              this.trackPerformanceEntry(entry);
            }
          });
        });
        
        observer.observe({ entryTypes: ['measure', 'navigation', 'resource'] });
      } catch (error) {
        console.warn('Performance Observer not fully supported:', error);
      }
    }
  }

  /**
   * Track performance metrics
   */
  trackPerformanceEntry(entry) {
    this.metrics.requestCount++;
    
    // Update average response time
    const currentAvg = this.metrics.averageResponseTime;
    const newTime = entry.duration || entry.responseEnd - entry.responseStart;
    this.metrics.averageResponseTime = (currentAvg + newTime) / 2;
    
    this.metrics.lastOperationTime = newTime;
  }

  /**
   * Monitor page visibility for performance optimization
   */
  setupVisibilityTracking() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Page hidden - pause non-critical operations
        this.pauseNonCriticalOperations();
      } else {
        // Page visible - resume operations
        this.resumeOperations();
      }
    });
  }

  pauseNonCriticalOperations() {
    // Increase batch delay when page is hidden
    this.batchDelay = 500;
  }

  resumeOperations() {
    // Restore normal batch delay
    this.batchDelay = 100;
  }

  /**
   * Get comprehensive performance metrics including ICR data
   */
  getPerformanceMetrics() {
    const sectionManager = window.cartSectionManager;
    const sectionMetrics = sectionManager ? sectionManager.getPerformanceMetrics() : {};
    
    return {
      ...this.metrics,
      batchEfficiency: this.metrics.batchedRequests > 0 
        ? ((this.metrics.batchedRequests - this.metrics.requestCount) / this.metrics.batchedRequests * 100).toFixed(1) + '%'
        : '0%',
      sectionCacheHitRatio: sectionMetrics.cacheHitRatio || '0%',
      pendingOperations: this.pendingOperations.size,
      
      // ICR-specific metrics (Phase 1)
      icr: {
        requestCount: this.icrMetrics.requestCount,
        successCount: this.icrMetrics.successCount,
        errorCount: this.icrMetrics.errorCount,
        successRate: this.icrMetrics.requestCount > 0 
          ? ((this.icrMetrics.successCount / this.icrMetrics.requestCount) * 100).toFixed(1) + '%'
          : '0%',
        averageResponseTime: this.icrMetrics.averageResponseTime.toFixed(2) + 'ms',
        lastRequestTime: this.icrMetrics.lastRequestTime.toFixed(2) + 'ms',
        adapterProductId: this.icrMetrics.adapterProductId,
        lineItemSourcesCount: this.icrMetrics.lineItemSources.size
      },
      
      timestamp: Date.now()
    };
  }

  /**
   * Real-time performance dashboard with ICR metrics
   */
  showPerformanceDashboard() {
    const metrics = this.getPerformanceMetrics();
    
    console.group('🚀 Advanced Cart Performance Dashboard');
    console.log('🔄 Request Batching:');
    console.log('  • Total Requests:', metrics.requestCount);
    console.log('  • Batched Requests:', metrics.batchedRequests);  
    console.log('  • Batch Efficiency:', metrics.batchEfficiency);
    
    console.log('⚡ Optimistic UI:');
    console.log('  • Optimistic Updates:', metrics.optimisticUpdates);
    console.log('  • Pending Operations:', metrics.pendingOperations);
    
    console.log('📊 Performance:');
    console.log('  • Average Response Time:', metrics.averageResponseTime.toFixed(2) + 'ms');
    console.log('  • Last Operation Time:', metrics.lastOperationTime.toFixed(2) + 'ms');
    console.log('  • Error Rate:', metrics.errorRate);
    console.log('  • Section Cache Hit Ratio:', metrics.sectionCacheHitRatio);
    
    // Phase 1: ICR Performance Metrics
    
    console.log('  • ICR Requests:', metrics.icr.requestCount);
    console.log('  • Success Rate:', metrics.icr.successRate);
    console.log('  • Average Response Time:', metrics.icr.averageResponseTime);
    console.log('  • Last Request Time:', metrics.icr.lastRequestTime);
    console.log('  • Adapter Product ID:', metrics.icr.adapterProductId || 'Not detected');
    console.log('  • Line Item Sources:', metrics.icr.lineItemSourcesCount);
    
    console.groupEnd();
  }

  /**
   * Update cart totals directly without section replacement
   */
  async updateCartTotalsDirectly(cartData) {
    try {
      if (window.cartPerformanceDebug) {
        console.log('💰 Attempting direct cart totals update...');
      }
      
      // Fetch fresh cart data if not provided
      if (!cartData || !cartData.items) {
        const cartResponse = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (!cartResponse.ok) {
          throw new Error('Failed to fetch cart data');
        }
        
        cartData = await cartResponse.json();
      }
      
      // Calculate totals
      const itemCount = cartData.item_count || 0;
      const totalPrice = cartData.total_price || 0;
      const formattedTotal = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(totalPrice / 100);
      
      if (window.cartPerformanceDebug) {
        console.log(`💰 Cart totals: ${itemCount} items, ${formattedTotal}`);
      }
      
      // Update total displays in footer
      const totalElements = document.querySelectorAll('.totals__subtotal-value, .cart__total-price, [data-cart-total]');
      totalElements.forEach(element => {
        if (element) {
          element.textContent = formattedTotal;
          if (window.cartPerformanceDebug) {
            console.log(`💰 Updated total element: ${element.className || element.tagName}`);
          }
        }
      });
      
      // Update cart count in bubble
      const countElements = document.querySelectorAll('.cart-count-bubble, [data-cart-count]');
      countElements.forEach(element => {
        if (element) {
          element.textContent = itemCount.toString();
          if (window.cartPerformanceDebug) {
            console.log(`🔢 Updated count element: ${element.className || element.tagName}`);
          }
        }
      });
      
      if (window.cartPerformanceDebug) {
        console.log('✅ Direct cart totals update completed');
      }
      
    } catch (error) {
      console.warn('❌ Failed to update cart totals directly:', error);
    }
  }

  // Phase 3: Methods for managing recent ICR completions to prevent unnecessary refreshes

  getRecentICRCompletions() {
    const cutoffTime = Date.now() - 2000; // Last 2 seconds
    const recentCompletions = [];
    
    for (const [lineItemKey, timestamp] of this.recentICRCompletions.entries()) {
      if (timestamp >= cutoffTime) {
        recentCompletions.push({
          lineItemKey: lineItemKey,
          timestamp: timestamp
        });
      }
    }
    
    if (window.cartPerformanceDebug && recentCompletions.length > 0) {
      console.log(`📋 Found ${recentCompletions.length} recent ICR completion(s):`, recentCompletions);
    }
    
    return recentCompletions;
  }

  markRecentICRCompletion(requestDetails) {
    if (!requestDetails.lineItemKey) return;
    
    const now = Date.now();
    this.recentICRCompletions.set(requestDetails.lineItemKey, now);

    // Clean up old entries (older than grace period)
    this.cleanupOldICRCompletions();
  }

  shouldSkipRefreshForICR(lineItemKey = null) {
    const now = Date.now();
    
    // If a specific line item key is provided, check just that one
    if (lineItemKey) {
      const completionTime = this.recentICRCompletions.get(lineItemKey);
      if (completionTime && (now - completionTime) < this.icrRefreshGracePeriod) {
  
        return true;
      }
      return false;
    }
    
    // Otherwise, check if ANY recent ICR completions should block refresh
    for (const [key, completionTime] of this.recentICRCompletions.entries()) {
      if ((now - completionTime) < this.icrRefreshGracePeriod) {

        return true;
      }
    }
    
    return false;
  }

  cleanupOldICRCompletions() {
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [key, completionTime] of this.recentICRCompletions.entries()) {
      if ((now - completionTime) >= this.icrRefreshGracePeriod) {
        this.recentICRCompletions.delete(key);
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      
    }
  }
}

// Initialize performance manager
if (!window.cartPerformanceManager) {
  
  window.cartPerformanceManager = new CartPerformanceManager();
} else {
  
}

// Debugging utilities
window.cartPerformanceManager.enableDebug = function() {
  window.cartPerformanceDebug = true;
  console.log('🔧 Advanced performance debugging enabled');
};

window.cartPerformanceManager.showDashboard = function() {
  window.cartPerformanceManager.showPerformanceDashboard();
};

// Phase 1: ICR-specific debugging utilities
window.cartPerformanceManager.showICRStats = function() {
  const metrics = window.cartPerformanceManager.getPerformanceMetrics();
  
  console.group('🔧 ICR Adapter Statistics (Phase 1)');
  console.log('📊 Request Summary:');
  console.log('  • Total ICR Requests:', metrics.icr.requestCount);
  console.log('  • Successful Requests:', metrics.icr.successCount);
  console.log('  • Failed Requests:', metrics.icr.errorCount);
  console.log('  • Success Rate:', metrics.icr.successRate);
  
  console.log('⚡ Performance:');
  console.log('  • Average Response Time:', metrics.icr.averageResponseTime);
  console.log('  • Last Request Time:', metrics.icr.lastRequestTime);
  
  console.log('🎯 Product Information:');
  console.log('  • Adapter Product ID:', metrics.icr.adapterProductId || 'Not detected yet');
  console.log('  • Line Item Sources Tracked:', metrics.icr.lineItemSourcesCount);
  
  if (window.cartPerformanceManager.icrMetrics.lineItemSources.size > 0) {
    console.log('📋 Line Item Sources:');
    window.cartPerformanceManager.icrMetrics.lineItemSources.forEach((data, lineItemKey) => {
      console.log(`  • ${lineItemKey}:`, {
        sourceProductId: data.productId,
        responseTime: data.duration.toFixed(2) + 'ms',
        addedAt: new Date(data.timestamp).toLocaleTimeString()
      });
    });
  }
  
  console.log('🔧 Debug Commands:');
  console.log('  • Enable ICR debug: window.cartPerformanceDebug = true');
  console.log('  • View all metrics: window.cartPerformanceManager.showDashboard()');
  console.log('  • Clear ICR stats: window.cartPerformanceManager.clearICRStats()');
  
  console.groupEnd();
};

window.cartPerformanceManager.clearICRStats = function() {
  window.cartPerformanceManager.icrMetrics = {
    requestCount: 0,
    successCount: 0,
    errorCount: 0,
    averageResponseTime: 0,
    lastRequestTime: 0,
    adapterProductId: null,
    lineItemSources: new Map()
  };
  console.log('🗑️ ICR statistics cleared');
};

window.cartPerformanceManager.enableICRDebug = function() {
  window.cartPerformanceDebug = true;
  
  console.log('💡 Add an ICR adapter to see the logging in action');
};

window.cartPerformanceManager.debugCartDrawers = function() {
  console.group('🔧 Cart Drawer Debug Information');
  
  const cartDrawers = document.querySelectorAll('cart-drawer');
  console.log(`📊 Found ${cartDrawers.length} cart-drawer elements in DOM`);
  
  cartDrawers.forEach((drawer, index) => {
    console.log(`\n🎯 Cart Drawer #${index + 1}:`);
    console.log('  • Element:', drawer);
    console.log('  • Instance ID:', drawer.instanceId || 'Not set');
    
    const icrContainers = drawer.querySelectorAll('.line-item-icr-upsell');
    console.log(`  • ICR Containers: ${icrContainers.length}`);
    
    icrContainers.forEach((container, containerIndex) => {
      console.log(`    - Container ${containerIndex + 1}:`, container);
      console.log(`      Line Item Key: ${container.dataset.lineItemKey}`);
      console.log(`      Product ID: ${container.dataset.productId}`);
    });
  });
  
  console.log(`\n🔧 Constructor call count: ${window.cartDrawerInstances || 0}`);
  console.groupEnd();
};

window.cartPerformanceManager.debugFetchOverride = function() {
  console.group('🔧 Fetch Override Debug Information');
  console.log('📊 Performance Manager Instances:', window.cartPerformanceManagerInstances || 0);
  console.log('🔧 Fetch Override Installed:', !!window.fetch._cartPerformanceWrapped);
  console.log('🔧 Original Fetch Available:', !!window.fetch._originalFetch);
  console.log('🔧 Current Fetch Function:', window.fetch.toString().substring(0, 200) + '...');
  
  // Check for known conflicts
  const fetchString = window.fetch.toString();
  const hasKlaviyo = fetchString.includes('klaviyo.com') || fetchString.includes('back-in-stock');
  const hasCartPerformance = fetchString.includes('handleICRRequest') || fetchString.includes('cart/add');
  
  console.log('🔧 Klaviyo Wrapper Detected:', hasKlaviyo);
  console.log('🔧 Cart Performance Wrapper Detected:', hasCartPerformance);
  
  if (window.fetch._cartPerformanceWrapped) {
    console.log('✅ Fetch override is properly installed');
  } else {
    console.warn('⚠️ Fetch override is NOT installed');
  }
  
  console.groupEnd();
};

window.cartPerformanceManager.forceInstallFetchOverride = function() {
  
  window.cartPerformanceManager.interceptCartRequests();
  
  // Verify installation
  if (window.fetch._cartPerformanceWrapped) {
    console.log('✅ Fetch override force installation successful!');
  } else {
    console.error('❌ Fetch override force installation failed!');
  }
};

window.cartPerformanceManager.clearICRDeduplication = function() {
  if (window.cartPerformanceManager._activeICRRequests) {
    window.cartPerformanceManager._activeICRRequests.clear();
    console.log('🗑️ ICR deduplication map cleared');
  } else {
    console.log('🗑️ ICR deduplication map was already empty');
  }
};

// Phase 3: Debugging utilities for recent ICR completions
window.cartPerformanceManager.showRecentICRCompletions = function() {
  console.group('🚀 PHASE 3: Recent ICR Completions');
  
  const now = Date.now();
  const completions = window.cartPerformanceManager.recentICRCompletions;
  
  if (completions.size === 0) {
    console.log('📝 No recent ICR completions tracked');
  } else {
    console.log(`📝 Tracking ${completions.size} recent completion(s):`);
    
    for (const [lineItemKey, completionTime] of completions.entries()) {
      const ageMs = now - completionTime;
      const ageSec = (ageMs / 1000).toFixed(1);
      const withinGracePeriod = ageMs < window.cartPerformanceManager.icrRefreshGracePeriod;
      
      console.log(`  • Line Item: ${lineItemKey}`);
      console.log(`    - Completed: ${ageSec}s ago`);
      console.log(`    - Within Grace Period: ${withinGracePeriod ? '✅ Yes' : '❌ No'}`);
    }
  }
  
  console.log(`⏱️ Grace Period: ${window.cartPerformanceManager.icrRefreshGracePeriod / 1000}s`);
  console.groupEnd();
};

window.cartPerformanceManager.clearRecentICRCompletions = function() {
  const count = window.cartPerformanceManager.recentICRCompletions.size;
  window.cartPerformanceManager.recentICRCompletions.clear();
  console.log(`🗑️ Cleared ${count} recent ICR completion(s)`);
};

window.cartPerformanceManager.testICRRefreshSkip = function(lineItemKey = null) {
  const shouldSkip = window.cartPerformanceManager.shouldSkipRefreshForICR(lineItemKey);
  
  if (lineItemKey) {
    console.log(`🧪 Testing ICR refresh skip for line item: ${lineItemKey}`);
  } else {
    console.log('🧪 Testing ICR refresh skip for any recent activity');
  }
  
  console.log(`Result: ${shouldSkip ? '⏭️ SKIP refresh' : '🔄 PROCEED with refresh'}`);
  return shouldSkip;
};

window.cartPerformanceManager.debugActiveRequests = function() {
  console.group('🔍 DEBUG: Active ICR Requests');
  
  const activeRequests = window.cartPerformanceManager._activeICRRequests;
  
  if (!activeRequests || activeRequests.size === 0) {
    console.log('📝 No active ICR requests');
  } else {
    console.log(`📝 ${activeRequests.size} active request(s):`);
    
    const now = Date.now();
    for (const [fingerprint, timestamp] of activeRequests.entries()) {
      const age = now - timestamp;
      console.log(`  • Fingerprint: ${fingerprint.substring(0, 100)}...`);
      console.log(`    - Age: ${age}ms`);
      console.log(`    - Expired: ${age > 5000 ? '✅ Yes' : '❌ No'}`);
    }
  }
  
  console.groupEnd();
};

window.cartPerformanceManager.testCartVisualUpdate = function() {
  console.group('🧪 Testing Cart Visual Update');
  
  // Trigger hideICRUpsellsForCompletedItems on both cart components
  const cartDrawer = document.querySelector('cart-drawer');
  const cartItems = document.querySelector('cart-items');
  
  console.log('🔍 Found cart components:');
  console.log('  - Cart Drawer:', cartDrawer ? '✅ Present' : '❌ Missing');
  console.log('  - Cart Items:', cartItems ? '✅ Present' : '❌ Missing');
  
  if (cartDrawer && typeof cartDrawer.hideICRUpsellsForCompletedItems === 'function') {
    console.log('🔄 Triggering cart drawer ICR upsell check...');
    cartDrawer.hideICRUpsellsForCompletedItems();
  }
  
  if (cartItems && typeof cartItems.hideICRUpsellsForCompletedItems === 'function') {
    console.log('🔄 Triggering main cart ICR upsell check...');
    cartItems.hideICRUpsellsForCompletedItems();
  }
  
  console.groupEnd();
};

window.cartPerformanceManager.debugICRUpsellStates = function() {
  console.group('🔍 DEBUG: ICR Upsell Container States');
  
  const allUpsells = document.querySelectorAll('.line-item-icr-upsell');
  console.log(`Found ${allUpsells.length} ICR upsell container(s):`);
  
  allUpsells.forEach((upsell, index) => {
    const lineItemKey = upsell.dataset.lineItemKey;
    const productId = upsell.dataset.productId;
    const isOptimistic = upsell.classList.contains('icr-optimistic-hiding');
    const isAdapterPresent = upsell.classList.contains('icr-adapter-present');
    const opacity = upsell.style.opacity;
    const maxHeight = upsell.style.maxHeight;
    
    console.log(`  ${index + 1}. Line Item: ${lineItemKey}`);
    console.log(`     - Product ID: ${productId}`);
    console.log(`     - Classes: ${upsell.className}`);
    console.log(`     - Optimistic Hidden: ${isOptimistic ? '✅ Yes' : '❌ No'}`);
    console.log(`     - Adapter Present: ${isAdapterPresent ? '✅ Yes' : '❌ No'}`);
    console.log(`     - Opacity: ${opacity || 'default'}`);
    console.log(`     - Max Height: ${maxHeight || 'default'}`);
    console.log(`     - Visible: ${upsell.offsetHeight > 0 ? '✅ Yes' : '❌ No'}`);
  });
  
  console.groupEnd();
};

window.cartPerformanceManager.testCartSectionUpdate = function() {
  console.group('🧪 Testing Cart Section Update After ICR');
  
  // Simulate ICR request details for testing
  const testRequestDetails = {
    lineItemKey: 'test-line-item',
    sourceProductId: '12345',
    adapterProductId: '67890'
  };
  
  console.log('🔄 Manually triggering cart section update...');
  console.log('📋 Test request details:', testRequestDetails);
  
  // Create a fake successful response
  const fakeResponse = new Response(JSON.stringify({
    success: true,
    message: 'Test ICR addition',
    items: []
  }), {
    status: 200,
    statusText: 'OK',
    headers: { 'Content-Type': 'application/json' }
  });
  
  // Call the update method
  window.cartPerformanceManager.updateCartSectionsAfterICR(fakeResponse, testRequestDetails)
    .then(() => {
      console.log('✅ Test completed successfully');
      console.groupEnd();
    })
    .catch((error) => {
      console.error('❌ Test failed:', error);
      console.groupEnd();
    });
};

window.cartPerformanceManager.debugCartPageStructure = function() {
  console.group('🔍 DEBUG: Cart Page Structure Analysis');
  
  // Analyze the cart page structure to understand the nesting issue
  const cartItemsElements = document.querySelectorAll('cart-items');
  console.log(`📊 Found ${cartItemsElements.length} cart-items element(s):`);
  
  cartItemsElements.forEach((element, index) => {
    console.log(`\n🎯 cart-items #${index + 1}:`);
    console.log(`  - Element:`, element);
    console.log(`  - Classes:`, element.className);
    console.log(`  - ID:`, element.id || 'None');
    console.log(`  - Parent:`, element.parentElement?.tagName || 'None');
    
    // Check for nested cart-items
    const nestedCartItems = element.querySelectorAll('cart-items');
    if (nestedCartItems.length > 0) {
      console.warn(`  ⚠️ NESTED STRUCTURE DETECTED! ${nestedCartItems.length} nested cart-items found`);
      nestedCartItems.forEach((nested, nestedIndex) => {
        console.log(`    - Nested #${nestedIndex + 1}:`, nested.className);
      });
    }
    
    // Check for title elements
    const titles = element.querySelectorAll('.title--primary, h1');
    console.log(`  - Title elements: ${titles.length}`);
    
    // Check for continue shopping links
    const continueLinks = element.querySelectorAll('a[href*="collections"]');
    console.log(`  - Continue shopping links: ${continueLinks.length}`);
    
    // Check for main cart sections
    const mainCartItems = element.querySelector('#main-cart-items');
    const mainCartFooter = element.querySelector('#main-cart-footer');
    console.log(`  - Contains main-cart-items: ${!!mainCartItems}`);
    console.log(`  - Contains main-cart-footer: ${!!mainCartFooter}`);
  });
  
  console.log('\n🔧 Recommended Actions:');
  if (cartItemsElements.length > 1) {
    console.log('  1. ⚠️ Multiple cart-items detected - check for nesting issues');
    console.log('  2. 🔄 Consider using window.cartSectionManager.clearCache() and refresh');
  }
  
  console.log('  3. 🧪 Test section update: window.cartPerformanceManager.testCartSectionUpdate()');
  console.log('  4. 🛒 Test cart drawer: window.cartPerformanceManager.testCartDrawerUpdate()');
  console.log('  5. 🔄 Test bidirectional sync: window.cartPerformanceManager.testBidirectionalSync()');
  console.log('  6. 🛒 Debug cart drawer targeting: window.cartPerformanceManager.debugCartDrawerTargeting()');
  console.log('  7. 🧪 Test cart drawer extraction: window.cartPerformanceManager.testCartDrawerExtraction()');
  console.log('  8. 🔍 Check section cache: window.cartSectionManager.showCacheContents()');
  
  console.groupEnd();
};

// Test cart drawer update specifically
window.cartPerformanceManager.testCartDrawerUpdate = function() {
  console.group('🛒 Testing Cart Drawer Update');
  
  // Target the active/visible cart drawer (not hidden duplicates)
  const cartDrawers = document.querySelectorAll('cart-drawer');
  let cartDrawer = null;
  
  if (cartDrawers.length > 1) {
    // Find the visible/active cart drawer
    cartDrawer = Array.from(cartDrawers).find(drawer => 
      !drawer.hasAttribute('hidden') && 
      drawer.offsetParent !== null && 
      window.getComputedStyle(drawer).display !== 'none'
    ) || cartDrawers[0]; // Fallback to first if none are clearly visible
    
    console.log(`🔍 Found ${cartDrawers.length} cart drawer elements, using the active one`);
  } else {
    cartDrawer = cartDrawers[0];
  }
  
  if (!cartDrawer) {
    console.log('❌ No cart drawer found');
    console.groupEnd();
    return;
  }
  
  console.log('🎯 Cart drawer found:', cartDrawer);
  console.log('📋 Current classes:', cartDrawer.className);
  console.log('🔍 Is empty?', cartDrawer.classList.contains('is-empty'));
  
  // Check for ICR products in current drawer
  const icrItems = cartDrawer.querySelectorAll('[data-variant-id="46698355491052"], .cart-item:has([title*="ICR"]), .cart-item:has([title*="adapter"])');
  console.log('🔌 Current ICR items in drawer:', icrItems.length);
  
  // Fetch current cart sections
  console.log('📡 Fetching cart drawer sections...');
  fetch(`${window.routes.cart_url}?sections=cart-drawer`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
  .then(response => response.json())
  .then(data => {
    console.log('📦 Cart drawer section data received:', !!data['cart-drawer']);
    
    if (data && data['cart-drawer']) {
      const sectionContent = data['cart-drawer'];
      console.log('📋 Section length:', sectionContent.length);
      console.log('🔌 Contains ICR:', sectionContent.toLowerCase().includes('icr'));
      console.log('🔌 Contains adapter:', sectionContent.toLowerCase().includes('adapter'));
      
      const normalizedData = { sections: data };
      const cartDrawerSections = [{ id: 'cart-drawer', selector: '.shopify-section' }];
      
      console.log('🔄 Updating cart drawer...');
      window.cartSectionManager.smartRender(cartDrawer, normalizedData, cartDrawerSections);
      console.log('✅ Cart drawer update completed');
      
      // Check ICR items after update
      setTimeout(() => {
        const newIcrItems = cartDrawer.querySelectorAll('[data-variant-id="46698355491052"], .cart-item:has([title*="ICR"]), .cart-item:has([title*="adapter"])');
        console.log('🔌 ICR items after update:', newIcrItems.length);
        console.groupEnd();
      }, 100);
    } else {
      console.warn('⚠️ No cart drawer section data available');
      console.groupEnd();
    }
  })
  .catch(error => {
    console.error('❌ Failed to fetch cart drawer sections:', error);
    console.groupEnd();
  });
};

// Test bidirectional synchronization
window.cartPerformanceManager.testBidirectionalSync = function() {
  console.group('🔄 Testing Bidirectional Cart Synchronization');
  
  // Target the active/visible cart drawer (not hidden duplicates)
  const cartDrawers = document.querySelectorAll('cart-drawer');
  let cartDrawer = null;
  
  if (cartDrawers.length > 1) {
    // Find the visible/active cart drawer
    cartDrawer = Array.from(cartDrawers).find(drawer => 
      !drawer.hasAttribute('hidden') && 
      drawer.offsetParent !== null && 
      window.getComputedStyle(drawer).display !== 'none'
    ) || cartDrawers[0]; // Fallback to first if none are clearly visible
    
    console.log(`🔍 Found ${cartDrawers.length} cart drawer elements, using the active one`);
  } else {
    cartDrawer = cartDrawers[0];
  }
  
  const mainCartItems = document.querySelector('#main-cart-items');
  
  console.log('🎯 Components found:');
  console.log(`  - Cart drawer: ${cartDrawer ? '✅' : '❌'} (${cartDrawers.length} total)`);
  console.log(`  - Main cart: ${mainCartItems ? '✅' : '❌'}`);
  
  if (!cartDrawer || !mainCartItems) {
    console.warn('⚠️ Missing required components for sync testing');
    console.groupEnd();
    return;
  }
  
  // Check current ICR items in both
  const drawerICRItems = cartDrawer.querySelectorAll('[data-variant-id="46698355491052"], .cart-item:has([title*="ICR"]), .cart-item:has([title*="adapter"])');
  const mainICRItems = mainCartItems.querySelectorAll('[data-variant-id="46698355491052"], .cart-item:has([title*="ICR"]), .cart-item:has([title*="adapter"])');
  
  console.log('📊 Current ICR item counts:');
  console.log(`  - Cart drawer: ${drawerICRItems.length}`);
  console.log(`  - Main cart: ${mainICRItems.length}`);
  
  // Check subscription status
  const cartItems = document.querySelector('cart-items');
  console.log('🔗 Sync mechanisms:');
  console.log(`  - Main cart subscription: ${cartItems && cartItems.cartUpdateUnsubscriber ? '✅ Active' : '❌ Inactive'}`);
  console.log(`  - Cart drawer publishing: ${cartDrawer.querySelector('cart-drawer-items') ? '✅ Available' : '❌ Missing'}`);
  
  // Test cart-update event manually
  console.log('🧪 Testing cart-update event dispatch...');
  
  const testEvent = {
    source: 'test-sync',
    timestamp: Date.now(),
    message: 'Manual sync test'
  };
  
  // Dispatch the event that cart drawer would send
  if (window.publish) {
    window.publish('cart-update', testEvent);
    console.log('📡 Dispatched cart-update event');
    
    setTimeout(() => {
      console.log('✅ Bidirectional sync test completed');
      console.log('💡 If main cart updated, the sync is working');
      console.log('💡 If not, check the subscription and event handling');
      console.groupEnd();
    }, 500);
  } else {
    console.warn('❌ Publish function not available');
    console.groupEnd();
  }
};

// Debug cart drawer targeting and duplication issues
window.cartPerformanceManager.debugCartDrawerTargeting = function() {
  console.group('🛒 Cart Drawer Targeting Debug');
  
  const cartDrawers = document.querySelectorAll('cart-drawer');
  console.log(`📊 Total cart-drawer elements found: ${cartDrawers.length}`);
  
  cartDrawers.forEach((drawer, index) => {
    console.log(`\n🎯 Cart Drawer #${index + 1}:`);
    console.log('  - Element:', drawer);
    console.log('  - Classes:', drawer.className);
    console.log('  - Hidden attribute:', drawer.hasAttribute('hidden'));
    console.log('  - OffsetParent:', drawer.offsetParent);
    console.log('  - Display style:', window.getComputedStyle(drawer).display);
    console.log('  - Visibility style:', window.getComputedStyle(drawer).visibility);
    console.log('  - ICR items inside:', drawer.querySelectorAll('[data-variant-id="46698355491052"], .cart-item:has([title*="ICR"]), .cart-item:has([title*="adapter"])').length);
    console.log('  - Total cart items:', drawer.querySelectorAll('.cart-drawer-item, .cart-item').length);
  });
  
  // Test which drawer our selection logic picks
  let selectedDrawer = null;
  if (cartDrawers.length > 1) {
    selectedDrawer = Array.from(cartDrawers).find(drawer => 
      !drawer.hasAttribute('hidden') && 
      drawer.offsetParent !== null && 
      window.getComputedStyle(drawer).display !== 'none'
    ) || cartDrawers[0];
  } else {
    selectedDrawer = cartDrawers[0];
  }
  
  if (selectedDrawer) {
    const selectedIndex = Array.from(cartDrawers).indexOf(selectedDrawer);
    console.log(`\n✅ Selected drawer: #${selectedIndex + 1}`);
    console.log('  - This is the drawer that will be updated by section manager');
  } else {
    console.log('\n❌ No drawer selected - this indicates a targeting issue');
  }
  
  console.groupEnd();
};

// Test cart drawer content extraction to prevent nesting
window.cartPerformanceManager.testCartDrawerExtraction = function() {
  console.group('🧪 Testing Cart Drawer Content Extraction');
  
  // Fetch current cart drawer section
  fetch(`${window.routes.cart_url}?sections=cart-drawer`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
  .then(response => response.json())
  .then(data => {
    if (data['cart-drawer']) {
      const sectionHTML = data['cart-drawer'];
      console.log('📦 Section HTML length:', sectionHTML.length);
      
      // Test different extraction methods
      console.log('\n🔍 Testing extraction methods:');
      
      // Method 1: .shopify-section (old way - causes nesting)
      const shopifySection = window.cartSectionManager.getSectionInnerHTML(sectionHTML, '.shopify-section');
      console.log('1. .shopify-section extraction:');
      console.log('   - Length:', shopifySection.length);
      console.log('   - Contains <cart-drawer>:', shopifySection.includes('<cart-drawer'));
      
      // Method 2: cart-drawer (new way - should prevent nesting)
      const cartDrawerInner = window.cartSectionManager.getSectionInnerHTML(sectionHTML, 'cart-drawer');
      console.log('2. cart-drawer extraction:');
      console.log('   - Length:', cartDrawerInner.length);
      console.log('   - Contains <cart-drawer>:', cartDrawerInner.includes('<cart-drawer'));
      console.log('   - Contains #CartDrawer:', cartDrawerInner.includes('id="CartDrawer"'));
      
      // Analysis
      if (shopifySection.includes('<cart-drawer') && !cartDrawerInner.includes('<cart-drawer')) {
        console.log('\n✅ GOOD: cart-drawer extraction prevents nesting');
        console.log('   - Old method includes <cart-drawer> wrapper (causes nesting)');
        console.log('   - New method extracts inner content only (prevents nesting)');
      } else if (cartDrawerInner.includes('<cart-drawer')) {
        console.log('\n⚠️ WARNING: cart-drawer extraction still includes wrapper');
        console.log('   - This may still cause nesting issues');
      } else if (cartDrawerInner.length === 0) {
        console.log('\n❌ ERROR: cart-drawer extraction returned empty content');
        console.log('   - Fallback to .shopify-section should be used');
      } else {
        console.log('\n✅ SUCCESS: cart-drawer extraction working correctly');
      }
      
    } else {
      console.log('❌ No cart-drawer section data received');
    }
    
    console.groupEnd();
  })
  .catch(error => {
    console.error('❌ Failed to fetch cart drawer section:', error);
    console.groupEnd();
  });
}; 